import React, { useEffect, useState } from 'react';
import { Users, Save } from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import { usersService } from 'api/services/users.service';

// Configure how new WhatsApp leads (first inbound from a new contact) are
// auto-assigned to handlers. Org-wide default config (per-number is supported by
// the API and can be layered on later).
const WhatsAppLeadAssignment: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [numbers, setNumbers] = useState<any[]>([]);
  const [configs, setConfigs] = useState<any[]>([]);
  const [scope, setScope] = useState<string>(''); // '' = org-wide default; else phoneNumberId
  const [enabled, setEnabled] = useState(true);
  const [strategy, setStrategy] = useState<'ROUND_ROBIN' | 'LEAST_BUSY' | 'SINGLE'>('ROUND_ROBIN');
  const [handlers, setHandlers] = useState<string[]>([]);
  // Kept apart from `msg`, which reports the save. A list that could not be
  // loaded is not a save result, and rendering it as one would clear the
  // moment somebody pressed Save.
  const [usersError, setUsersError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Load the form from the config matching the chosen scope (number or org-default).
  const applyScope = (s: string, cfgs: any[]) => {
    const cfg = cfgs.find((c: any) => (s ? c.phoneNumberId === s : !c.phoneNumberId));
    setEnabled(cfg?.enabled ?? true);
    setStrategy((cfg?.strategy || 'ROUND_ROBIN') as any);
    try { setHandlers(JSON.parse(cfg?.handlerUserIds || '[]')); } catch { setHandlers([]); }
  };

  useEffect(() => {
    (async () => {
      const u = await usersService.getUsers({ perPage: 200 });
      if (u.success && u.data) setUsers((u.data as any).data || (u.data as any).content || []);
      // Say so. This failed silently for every business user — the request was
      // refused and the box just read "No users.", which describes an empty
      // company rather than a refused question, and so was never reported as
      // the bug it was.
      else setUsersError(u.error || 'Could not load the list of users.');
      const nums = await whatsappService.getAllConfigs();
      if (nums.success && Array.isArray(nums.data)) setNumbers(nums.data);
      const cfgRes = await whatsappService.getLeadConfigs();
      const cfgs = cfgRes.success && Array.isArray(cfgRes.data) ? cfgRes.data : [];
      setConfigs(cfgs);
      applyScope('', cfgs);
      setLoading(false);
    })();
  }, []);

  const onScopeChange = (s: string) => { setScope(s); setMsg(null); applyScope(s, configs); };

  // Handlers saved into the config who are no longer in this organisation.
  // CatchMyCall Business has one — apps@catchmycall.ai, which moved to the
  // parent org in the split — and round-robin has kept feeding it leads ever
  // since. Without this the count says "2 selected" while one checkbox is
  // ticked, and the missing one has no row to untick.
  const offTeam = handlers.filter((id) => !users.some((u) => String(u.id) === id));

  const toggleHandler = (id: string) =>
    setHandlers((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id]));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    const res = await whatsappService.saveLeadConfig({ phoneNumberId: scope || null, strategy, enabled, handlerUserIds: handlers });
    if (res.success && res.data) {
      // refresh local config cache so switching scopes reflects the save
      setConfigs((prev) => {
        const others = prev.filter((c: any) => (scope ? c.phoneNumberId !== scope : c.phoneNumberId));
        return [...others, res.data];
      });
    }
    setMsg(res.success ? 'Saved ✓' : (res.error || 'Failed to save'));
    setSaving(false);
  };

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lead Auto-Assignment</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">New WhatsApp leads are routed to handlers automatically.</p>
      </div>

      {loading ? <p className="text-gray-500">Loading…</p> : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Applies to</label>
            <select
              value={scope}
              onChange={(e) => onScopeChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
            >
              <option value="">Org-wide default (all numbers)</option>
              {numbers.map((n: any) => (
                <option key={n.phoneNumberId} value={n.phoneNumberId}>
                  {n.label || n.phoneNumber || n.phone_number || n.phoneNumberId}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">A number-specific config overrides the org default for that number.</p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="text-gray-800 dark:text-gray-200 font-medium">Enable auto-assignment</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
            >
              <option value="ROUND_ROBIN">Round-robin (cycle evenly)</option>
              <option value="LEAST_BUSY">Least-busy (fewest open leads)</option>
              <option value="SINGLE">Single handler (first in list)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" /> Lead handlers ({handlers.length} selected)
            </label>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-72 overflow-y-auto">
              {users.length === 0 ? (
                <p className="p-3 text-sm" style={{ color: usersError ? 'var(--danger-text)' : undefined }}>
                  {usersError
                    ? `${usersError} Lead handlers cannot be chosen until it loads.`
                    : 'Nobody in this organisation yet — add a user first.'}
                </p>
              ) :
                users.map((u) => {
                  const id = String(u.id);
                  const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || u.email || id;
                  return (
                    <label key={id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                      <input type="checkbox" checked={handlers.includes(id)} onChange={() => toggleHandler(id)} />
                      <span className="text-gray-800 dark:text-gray-200">{name}</span>
                      {u.role && <span className="text-xs text-gray-400">{u.role}</span>}
                    </label>
                  );
                })}
            </div>
            {users.length > 0 && offTeam.length > 0 && (
              <div className="mt-2 text-sm" style={{ color: 'var(--danger-text)' }}>
                <p>
                  {offTeam.length === 1 ? 'One handler is' : `${offTeam.length} handlers are`}{' '}
                  no longer in this organisation, so {offTeam.length === 1 ? 'it has' : 'they have'}{' '}
                  no row above — but {offTeam.length === 1 ? 'it is' : 'they are'} still being given
                  leads.
                </p>
                <ul className="mt-1 space-y-1">
                  {offTeam.map((id) => (
                    <li key={id} className="flex items-center gap-2">
                      <code className="text-xs">{id}</code>
                      <button
                        type="button"
                        onClick={() => toggleHandler(id)}
                        className="tl-btn tl-btn-neutral tl-btn-sm"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs">Removing takes effect when you press Save.</p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving}
              className="tl-btn tl-btn-primary disabled:opacity-50">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
            </button>
            {msg && <span className="text-sm text-gray-600 dark:text-gray-300">{msg}</span>}
          </div>
        </>
      )}
    </div>
  );
};

export default WhatsAppLeadAssignment;
